import { useCallback, useState } from 'react';
import { replaceUserEndpoint } from '../utils/userEndpoint';
import { patchFriendChallenge, patchHouseholdChallenge } from '../utils/challengeWireApi';
import { buildOptimisticDayTracking } from '../utils/challengeDayTracking';

function dayCheckKey(challenge, isoDate) {
	return `${challenge.kind}-${challenge.id}-${isoDate}`;
}

export default function useChallengeDayChecks({ user, csrfToken, endpoints }) {
	const [pendingKeys, setPendingKeys] = useState(() => new Set());

	const setPending = useCallback((key, pending) => {
		setPendingKeys((prev) => {
			const next = new Set(prev);
			if (pending) next.add(key);
			else next.delete(key);
			return next;
		});
	}, []);

	const toggleDayCheck = useCallback(
		async (challenge, isoDate, checked, { onUpdate } = {}) => {
			if (!user?.id || !challenge || !isoDate) return;

			const key = dayCheckKey(challenge, isoDate);
			if (pendingKeys.has(key)) return;

			const previousTracking = challenge.day_tracking;
			const optimisticTracking = buildOptimisticDayTracking(previousTracking, challenge, isoDate, checked);

			if (onUpdate) onUpdate(challenge, optimisticTracking);
			setPending(key, true);

			try {
				const endpointMap =
					challenge.kind === 'friend'
						? endpoints.day_check_user_fitness_challenge_endpoint
						: endpoints.day_check_user_fitness_profile_challenge_endpoint;

				const url = replaceUserEndpoint(endpointMap, { userId: user.id, id: challenge.id });
				const patch = challenge.kind === 'friend' ? patchFriendChallenge : patchHouseholdChallenge;
				const response = await patch({
					endpoint: url,
					csrfToken,
					body: { date: isoDate, checked }
				});

				if (onUpdate && response?.data?.day_tracking) {
					onUpdate(challenge, response.data.day_tracking);
				}
			} catch (error) {
				if (onUpdate) onUpdate(challenge, previousTracking);
				throw error;
			} finally {
				setPending(key, false);
			}
		},
		[user?.id, csrfToken, endpoints, pendingKeys, setPending]
	);

	const isDayCheckPending = useCallback(
		(challenge, isoDate) => pendingKeys.has(dayCheckKey(challenge, isoDate)),
		[pendingKeys]
	);

	return { toggleDayCheck, isDayCheckPending };
}
